import React, {useCallback, useMemo, useState} from "react"
import {DropdownMenu} from "scholastic-client-components/src/ui/DropdownSelect/DropdownMenu/DropdownMenu"
import {OverflowTextHint} from "scholastic-client-components/src/ui/OverflowTextHint/OverflowTextHint"
import styles from "./ChangeLanguage.module.scss"
import {Box} from "scholastic-client-components/src/ui/Box/Box"
import {Text} from "scholastic-client-components/src/ui/Text/Text"
import {useTranslation} from "react-i18next"
import {classNames} from "scholastic-client-components/src/SharedPackages/classnames"
import {LocalStorageLocale} from "scholastic-client-components/src/i18n/localStorage"
import {MonochromeIcon} from "scholastic-client-components/src/ui/MonochromeIcon/MonochromeIcon"
import {Position} from "scholastic-client-components/src/ui/DropdownBase/DropdownBase"
import {PersonLocale} from "scholastic-client-components/src/slices/authSlice"
import {useSelector} from "react-redux"
import {changeLanguageSelectors} from "../../slices/changeLanguageSlice"
import {APIStatus} from "scholastic-client-components/src/types/APITypes"
import {Loader} from "scholastic-client-components/src/ui/Loader/Loader"
import {DEFAULT_LANGUAGE} from "scholastic-client-components/src/i18n/i18n"

interface ChangeLanguageProps {
    locales?: Array<{id: string; name: string}>
    position?: Position
    shouldReloadPage?: boolean
    onLanguageChange?: (param: PersonLocale) => void
}

export const ChangeLanguage: React.FC<ChangeLanguageProps> = ({
    locales,
    position = "bottom right",
    shouldReloadPage = true,
    onLanguageChange,
}) => {
    const {t, i18n} = useTranslation(["person"])
    const localesStatus = useSelector(changeLanguageSelectors.getStatus)
    const unauthorizedUserLocale = LocalStorageLocale.getValue() || DEFAULT_LANGUAGE

    const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
        unauthorizedUserLocale
    )

    const handleChangeLanguage = useCallback(
        async (item: PersonLocale) => {
            LocalStorageLocale.setValue(item.id)
            setSelectedLanguage(locales?.find((locale) => locale.id === item.id)?.id)

            if (shouldReloadPage) {
                window.location.reload()
            } else {
                try {
                    await i18n.changeLanguage(item.id)
                } catch (error) {
                    console.error("Error changing language:", error)
                }
            }

            if (onLanguageChange) {
                onLanguageChange(item)
            }
        },
        [i18n, locales, shouldReloadPage, onLanguageChange]
    )

    const languageOptions = useMemo(() => {
        return locales?.map((item) => ({
            id: item.id,
            label: item.name,
            element: (
                <div
                    onClick={() => handleChangeLanguage(item)}
                    className={classNames(styles.dropdownItem, {
                        [styles.selected]: item.id === selectedLanguage,
                    })}
                >
                    <OverflowTextHint text={item.name} baseMaxWidth={"100%"} closeOnClick={false} />
                </div>
            ),
        }))
    }, [locales, selectedLanguage, handleChangeLanguage])

    return (
        <Box flex={{alignItems: "center"}} propsStyles={{columnGap: "1rem"}}>
            <Text hex={"#666666"}>{t("person:fieldname.language")}:</Text>
            <DropdownMenu
                baseDropdownProps={{disabled: localesStatus === APIStatus.Loading}}
                position={position}
                className={styles.dropdownTrigger}
                resetOptionStyles
                label={
                    <Box flex={{alignItems: "center"}}>
                        {localesStatus === APIStatus.Loading ? (
                            <Loader multiColor inline size={1.125} />
                        ) : (
                            <MonochromeIcon.Earth className={styles.earthIcon} />
                        )}
                        {selectedLanguage?.toUpperCase()}
                    </Box>
                }
                fullWidthBlueTrigger
                items={languageOptions}
                widthList={264}
                withScrollbars
                dataTest={"language-dropdown"}
            />
        </Box>
    )
}
